/**
 * Grouping, for the screens.
 *
 * The team's unit of work is the deal a retailer prices, not the site: sites belonging to one
 * legal entity, in one state, coming off contract on one date are quoted together and assessed
 * together. Their rule, in their words:
 *
 *     1 group = 1 assessment = 1 Best Offers + 1 Cost Analysis + 1 List of Offers
 *
 * ⚠️ THE KEY HERE MUST MATCH THE SERVER'S, CHARACTER FOR CHARACTER.
 *
 * proposalContextService.siteGroupKey builds the same string from the database row — `abn`,
 * `addr_state`, `current_contract_expiry`. This builds it from the form's own Site, whose fields
 * are named differently (`address.state`, `currentContractExpiry`) and whose date is a Date.
 * If the two ever disagree, Stage 4 will offer a group the proposal does not print, which is
 * worse than having no grouping on the screens at all. `siteGroupKeyParts` is the one place the
 * rule lives; both sides call it with whatever shape they hold.
 *
 * Of the four criteria the team named, **Term** is missing: a site has nowhere to record the
 * terms it wants, so there is nothing to group on. The other three are applied. Adding the
 * fourth means a new field on the site, which is the part of F1 still outstanding.
 */

/** A site, as little of it as grouping needs. */
export type GroupableSite = {
  id: string;
  nmi?: string | null;
  name?: string | null;
  abn?: string | null;
  address?: { state?: string | null } | null;
  currentContractExpiry?: Date | string | null;
};

const norm = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** A date as `YYYY-MM-DD`, whatever shape it arrives in. Blank when there is no date. */
const isoDay = (v: Date | string | null | undefined): string => {
  if (!v) return '';
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10);
  return String(v).trim().slice(0, 10);
};

/**
 * The three parts of the key, in order.
 *
 * ⚠️ A SITE WITH NO ABN GROUPS WITH NOBODY. Two blanks are not evidence of one company, and
 * merging them would sum the usage of two unrelated businesses onto one page. Its own id keeps
 * it apart — the same guard the server's key uses.
 */
export const siteGroupKeyParts = (abn: unknown, state: unknown, expiry: unknown, fallbackId: unknown): string[] => [
  norm(abn) || `\u0000no-abn:${norm(fallbackId)}`,
  norm(state),
  isoDay(expiry as Date | string | null | undefined),
];

/** The group a form site belongs to. Equal strings mean one deal. */
export const siteGroupKey = (site: GroupableSite): string =>
  siteGroupKeyParts(site.abn, site.address?.state, site.currentContractExpiry, site.id ?? site.nmi).join('|');

/** One group of sites, in the order the tender lists them. */
export type SiteGroup<T extends GroupableSite = GroupableSite> = {
  key: string;
  sites: T[];
  /** Every NMI in the group, for the "NMI: a, b, c" line the team asked for. */
  nmis: string[];
  /** The entity this group is, taken from the first site that names itself. */
  entityName: string;
  abn: string;
  state: string;
};

/**
 * Bucket sites into groups, keeping first-appearance order.
 *
 * Order matters beyond tidiness: the proposal clones its pages in this order and stamps each
 * clone with its index, so a screen that orders groups differently would label the wrong page.
 */
export const groupSites = <T extends GroupableSite>(sites: readonly T[]): SiteGroup<T>[] => {
  const byKey = new Map<string, SiteGroup<T>>();
  for (const site of sites ?? []) {
    if (!site) continue;
    const key = siteGroupKey(site);
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        sites: [],
        nmis: [],
        entityName: '',
        abn: String(site.abn ?? '').trim(),
        state: String(site.address?.state ?? '').trim(),
      };
      byKey.set(key, group);
    }
    group.sites.push(site);
    const nmi = String(site.nmi ?? '').trim();
    if (nmi) group.nmis.push(nmi);
    if (!group.entityName) group.entityName = String(site.name ?? '').trim();
  }
  return [...byKey.values()];
};

/**
 * What a group is called on screen: the name the team gave it, else the entity, else its NMIs.
 *
 * The team asked to name groups themselves — *"We can be the ones to name each group"* — but a
 * group has to be identifiable before anyone has named one, and most never will be.
 */
export const groupLabel = (group: SiteGroup, savedNames?: Record<string, string> | null): string => {
  const given = savedNames?.[group.key]?.trim();
  if (given) return given;
  if (group.entityName) return group.entityName;
  return group.nmis.join(', ') || 'Unnamed group';
};

/** "3 sites" / "1 site", for the dropdown line their mock-up shows. */
export const groupSiteCount = (group: SiteGroup): string =>
  `${group.sites.length} site${group.sites.length === 1 ? '' : 's'}`;
